import { Radio } from '@ads/components-react';
import type { ComponentType } from 'react';
import { cn } from '@/shared/utils/cn';
import type { NotificationSendMode } from '../../../types/notification';

export interface SendModeCardProps {
  readonly value: NotificationSendMode;
  readonly selected: boolean;
  readonly icon: ComponentType<{ className?: string }>;
  readonly title: string;
  readonly description: string;
  readonly onSelect: (value: NotificationSendMode) => void;
}

/**
 * Card selecionável para escolha do modo de envio da notificação.
 * Deve ser usado dentro de um `RadioGroup` do ADS.
 * O card inteiro funciona como botão — hover, focus e clique selecionam a opção.
 *
 * @param value - Valor do modo de envio (usado pelo RadioGroup para estado visual do Radio)
 * @param selected - Controla o estilo de destaque da borda
 * @param icon - Ícone exibido ao lado do título
 * @param title - Título do modo de envio
 * @param description - Descrição curta do modo de envio
 * @param onSelect - Callback ao selecionar o card
 */
export function SendModeCard({
  value,
  selected,
  icon: Icon,
  title,
  description,
  onSelect,
}: SendModeCardProps) {
  return (
    <button
      type="button"
      onClick={() => onSelect(value)}
      className={[
        'flex w-full items-start gap-micro rounded-md border-hairline py-xxs px-mini text-left transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ctx-content-title bg-ctx-layout-body',
        selected ? 'border-ctx-highlight-focus' : 'border-ctx-interactive-secondary',
      ].join(' ')}
    >
      <div className="mt-quark shrink-0 pointer-events-none" tabIndex={-1} aria-hidden="true">
        <Radio value={value} label="" size="sm" />
      </div>
      <div className="flex flex-col gap-quark">
        <div className="flex items-center gap-nano">
          <Icon className="h-xxxs w-xxxs shrink-0 text-ctx-content-title" />
          <span className={cn('ts-subtitle-xs text-ctx-content-title')}>{title}</span>
        </div>
        <span className="ts-subtitle-xxs text-ctx-content-base">{description}</span>
      </div>
    </button>
  );
}
